import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from '../../../components/idsk';
import { RuleSummarizer, flatten, mergeDefinition, type EvaluationContext, type FieldDef, type FormDefinition, type RuleName } from '../../../form-engine';
import { canBeRequired, text } from '../../../form-engine/types';
import type { BuilderMode } from './FieldEditor';
import { isGroup } from './palette';

export interface FieldListProps {
  mode: BuilderMode;
  /** template mode: the definition; custom mode: the pinned template definition */
  definition: FormDefinition;
  customFields: FieldDef[];
  context: EvaluationContext;
}

/**
 * Read-only overview of the fields of a form version: order, label and key, type, required flag and the rules in
 * words. Fields are edited on the canvas of the draft (FormCanvas) and in its properties pane.
 */
export function FieldList({ mode, definition, customFields, context }: FieldListProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
  const effective = mode === 'template' ? definition : mergeDefinition(definition, customFields);
  const summarizer = new RuleSummarizer(effective, context);
  const label = (f: FieldDef) => text(f.label, lang);
  const required = (f: FieldDef) => (!canBeRequired(f.type) ? '–' : f.required ? t('common.yes') : f.requiredWhen ? t('builder.list.conditional') : t('common.no'));

  const rows = (fields: FieldDef[], depth: number): React.ReactNode[] =>
    fields.map((f, index) => (
      <Fragment key={f.key}>
        <tr className="govuk-table__row">
          <td className="govuk-table__cell">{depth > 0 ? `${index + 1}.` : index + 1}</td>
          <th scope="row" className="govuk-table__header">
            {depth > 0 && <span className="govuk-visually-hidden">{t('builder.list.inGroup')} </span>}
            <span style={{ paddingLeft: depth ? '1.5em' : undefined }} lang={!f.label.en && lang === 'en' ? 'sk' : undefined}>
              {depth > 0 && <span aria-hidden="true">↳ </span>}
              {label(f)}
            </span>
            {f.origin === 'INSPECTION' && (
              <>
                {' '}
                <Tag colour="purple">{t('builder.list.customTag')}</Tag>
              </>
            )}
            {f.chart && (
              <>
                {' '}
                <Tag colour="turquoise">{t('builder.canvas.chartTag')}</Tag>
              </>
            )}
            <br />
            <span className="govuk-hint govuk-!-font-size-16">{f.key}</span>
            {isGroup(f) && !f.children?.length && (
              <>
                <br />
                <span className="govuk-hint govuk-!-font-size-16">{t('builder.list.emptyGroup')}</span>
              </>
            )}
          </th>
          <td className="govuk-table__cell">{t(`builder.types.${f.type}.name`)}</td>
          <td className="govuk-table__cell">{required(f)}</td>
          <td className="govuk-table__cell">
            <ul className="govuk-list govuk-!-font-size-16">
              {(['visibleWhen', 'enabledWhen', 'requiredWhen'] as RuleName[]).map((r) => {
                const s = summarizer.summaryOf(f, r, lang);
                return s ? <li key={r}>{s}</li> : null;
              })}
            </ul>
          </td>
        </tr>
        {f.children && f.children.length > 0 && rows(f.children, depth + 1)}
      </Fragment>
    ));

  return (
    <div className="app-table-container">
      <table className="govuk-table">
        <caption className="govuk-table__caption govuk-table__caption--m">{t('builder.list.caption', { count: flatten(effective.fields).length })}</caption>
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header">
              {t('builder.list.position')}
            </th>
            <th scope="col" className="govuk-table__header">
              {t('builder.list.label')}
            </th>
            <th scope="col" className="govuk-table__header">
              {t('builder.list.type')}
            </th>
            <th scope="col" className="govuk-table__header">
              {t('builder.list.required')}
            </th>
            <th scope="col" className="govuk-table__header">
              {t('builder.list.rules')}
            </th>
          </tr>
        </thead>
        <tbody className="govuk-table__body">{rows(effective.fields, 0)}</tbody>
      </table>
    </div>
  );
}
